import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text, Html, useCursor, Float, Environment, ContactShadows, PerspectiveCamera, OrbitControls } from '@react-three/drei';

// ... (inside ArcadeCabinet component)

import * as THREE from 'three';

// --- Assets & Config ---
const CABINET_COLOR = '#1e293b';
const ACCENT_COLOR = '#22d3ee'; // Cyan
const MARQUEE_COLOR = '#f472b6'; // Pink
const SCREEN_GLOW = '#a5f3fc';

const GameListItem = ({ game, onSelect }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <div
            className={`
                relative flex items-center justify-center p-3 mb-2 rounded border-2 cursor-pointer transition-all duration-200
                ${hovered
                    ? 'bg-cyan-500/20 border-cyan-400 text-cyan-100 scale-105 shadow-[0_0_15px_rgba(34,211,238,0.5)]'
                    : 'bg-slate-900/80 border-slate-700 text-slate-400 hover:border-slate-500'
                }
            `}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => onSelect(game.id)}
        >
            <div className="flex flex-col text-center">
                <span className={`font-black text-lg tracking-wider ${hovered ? 'text-cyan-300' : 'text-slate-300'}`} style={{ fontFamily: '"Press Start 2P", monospace' }}>
                    {game.title.toUpperCase()}
                </span>
            </div>
            {hovered && <div className="absolute right-4 animate-pulse text-cyan-400">▶</div>}
        </div>
    );
};

const CabinetModel = ({ games, onSelectGame }) => {
    // Cabinet Dimensions
    const width = 14;
    const height = 24;
    const depth = 12;

    return (
        <group position={[0, -8, 0]}>
            {/* --- Main Body Structure --- */}

            {/* Side Panels */}
            <mesh position={[-width / 2 - 0.5, height / 2, 0]}>
                <boxGeometry args={[1, height, depth]} />
                <meshStandardMaterial color="#475569" roughness={0.1} metalness={0.9} />
            </mesh>
            <mesh position={[width / 2 + 0.5, height / 2, 0]}>
                <boxGeometry args={[1, height, depth]} />
                <meshStandardMaterial color="#475569" roughness={0.1} metalness={0.9} />
            </mesh>

            {/* Back Panel */}
            <mesh position={[0, height / 2, -depth / 2 + 0.5]}>
                <boxGeometry args={[width, height, 1]} />
                <meshStandardMaterial color={CABINET_COLOR} />
            </mesh>

            {/* Top Marquee Box */}
            <mesh position={[0, height - 2, depth / 2 - 2]}>
                <boxGeometry args={[width, 4, 4]} />
                <meshStandardMaterial color={CABINET_COLOR} />
            </mesh>

            {/* Roof Panel */}
            <mesh position={[0, height - 0.5, 0]}>
                <boxGeometry args={[width, 1, depth]} />
                <meshStandardMaterial color={CABINET_COLOR} />
            </mesh>

            {/* Marquee Text (Glowing) */}
            <group position={[0, height - 2, depth / 2 + 0.1]}>
                <Text
                    fontSize={1.2}
                    color={MARQUEE_COLOR}
                    anchorX="center"
                    anchorY="middle"
                >
                    BRAIN BREAK
                    <meshStandardMaterial emissive={MARQUEE_COLOR} emissiveIntensity={2} toneMapped={false} />
                </Text>
                {/* Brain Logos */}
                <Text position={[-5.5, 0, 0]} fontSize={1.5} color={ACCENT_COLOR}>🧠</Text>
                <Text position={[5.5, 0, 0]} fontSize={1.5} color={ACCENT_COLOR}>🧠</Text>
            </group>

            {/* Control Panel (Shelf) */}
            <group position={[0, height / 2 - 2, depth / 2 + 2]}>
                <mesh rotation={[0.5, 0, 0]}>
                    <boxGeometry args={[width, 1, 6]} />
                    <meshStandardMaterial color="#0f172a" />
                </mesh>
                {/* Joystick */}
                <group position={[-4, 1, 1]} rotation={[0.5, 0, 0]}>
                    <mesh position={[0, 1.5, 0]}>
                        <sphereGeometry args={[0.8]} />
                        <meshStandardMaterial color="#ef4444" />
                    </mesh>
                    <mesh position={[0, 0.5, 0]}>
                        <cylinderGeometry args={[0.2, 0.2, 1.5]} />
                        <meshStandardMaterial color="#94a3b8" />
                    </mesh>
                </group>
                {/* Buttons */}
                <mesh position={[2, 0.5, 1]} rotation={[0.5, 0, 0]}>
                    <cylinderGeometry args={[0.6, 0.6, 0.5]} />
                    <meshStandardMaterial color="#22d3ee" />
                </mesh>
                <mesh position={[4, 0.5, 0.5]} rotation={[0.5, 0, 0]}>
                    <cylinderGeometry args={[0.6, 0.6, 0.5]} />
                    <meshStandardMaterial color="#f472b6" />
                </mesh>
            </group>

            {/* Screen Area */}
            <group position={[0, height / 2 + 4, depth / 2 - 2]} rotation={[-0.2, 0, 0]}>
                {/* Screen Frame */}
                <mesh position={[0, 0, -0.5]}>
                    <boxGeometry args={[width - 1, 10, 1]} />
                    <meshStandardMaterial color="#000000" />
                </mesh>

                {/* The Interactive Screen (HTML) */}
                <Html
                    transform
                    occlude
                    position={[0, 0, 0.1]}
                    style={{ width: '400px', height: '300px', backgroundColor: '#000', borderRadius: '4px' }}
                >
                    <div className="w-full h-full p-4 overflow-y-auto bg-slate-950 border-4 border-slate-800 shadow-[inset_0_0_20px_rgba(0,0,0,0.8)] relative">
                        {/* CRT Scanline Overlay */}
                        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_2px,3px_100%] opacity-20"></div>

                        <h2 className="text-center text-cyan-400 mb-2 text-xl animate-pulse" style={{ fontFamily: '"Press Start 2P", monospace', textShadow: '0 0 10px #22d3ee' }}>
                            SELECT GAME
                        </h2>

                        <div className="space-y-2 relative z-20">
                            {games.map(game => (
                                <GameListItem key={game.id} game={game} onSelect={onSelectGame} />
                            ))}
                        </div>
                    </div>
                </Html>
            </group>

            {/* Base/Kickplate */}
            <mesh position={[0, 2, depth / 2 - 2]}>
                <boxGeometry args={[width, 4, 1]} />
                <meshStandardMaterial color="#334155" />
            </mesh>
        </group>
    );
};

const ArcadeCabinet = ({ games, onSelectGame }) => {
    return (
        <div className="w-full h-full bg-slate-900 relative touch-pan-y">
            <Canvas shadows>
                <PerspectiveCamera makeDefault position={[0, 10, 39]} fov={45} />
                <OrbitControls target={[0, 4, 0]} minPolarAngle={0} maxPolarAngle={Math.PI / 2} enableZoom={false} enablePan={false} />
                <color attach="background" args={['#0f172a']} />

                {/* Lighting */}
                <ambientLight intensity={0.5} />
                <pointLight position={[10, 10, 10]} intensity={1} color="#22d3ee" />
                <pointLight position={[-10, 10, 10]} intensity={1} color="#f472b6" />
                <spotLight position={[0, 50, 0]} angle={0.5} penumbra={1} intensity={2} castShadow />

                {/* Environment */}
                <Environment preset="night" />
                <ContactShadows position={[0, -8, 0]} opacity={0.5} scale={40} blur={2} far={4.5} />

                {/* Floating Cabinet */}
                <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}>
                    <CabinetModel games={games} onSelectGame={onSelectGame} />
                </Float>

                {/* Floor Grid (Tron style) */}
                <gridHelper args={[100, 20, '#1e293b', '#0f172a']} position={[0, -10, 0]} />
            </Canvas>

            {/* Overlay Instructions */}
            <div className="absolute bottom-8 left-0 w-full text-center pointer-events-none">
                <p className="text-slate-500 font-mono text-xs tracking-widest animate-pulse">
                    CLICK SCREEN TO SELECT • DRAG TO ROTATE
                </p>
            </div>
        </div>
    );
};

export default ArcadeCabinet;
